// Content schemas that mirror the actual website structure.
// Each schema defines the fields for a section of a page.
// Placeholders match the website's useContent() defaults so admins
// can see what currently displays and edit it rather than starting
// from scratch.

export type FieldType = 'text' | 'textarea' | 'image' | 'number' | 'select' | 'video';

/** Controls how the image preview container is shaped in the admin editor. */
export type PreviewStyle = 'hero' | 'photo';

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  hint?: string;
  /** For type=image: shape the preview to match the website display. */
  previewStyle?: PreviewStyle;
  /** For type=select: the available options. */
  options?: { value: string; label: string }[];
  /** For type=textarea: offer the formatting bar on Club and Pro. The server
   *  must list the same section and field in lambda/lib/rich-body.ts, or the
   *  HTML is stored unsanitised and the website shows it as text. */
  rich?: boolean;
  /** For type=select: fill the options at render time instead of listing them.
   *  'teamsWithFixtures' lists only sides that actually have a future match, so
   *  a club cannot pick a team whose block would then never appear. */
  optionsSource?: 'teamsWithFixtures' | 'teams';
}

export interface SectionSchema {
  id: string;
  name: string;
  page: string;
  description: string;
  fields: FieldDef[];
}

// Sidebar order: Home → Teams → News → Home Ground → Join Us → Shop → Safeguarding → Footer/Contact → Site-wide
// This matches the website navigation menu order.
export const PAGE_SECTIONS: SectionSchema[] = [
  // ── Home Page ──────────────────────────────────
  {
    id: 'home-hero',
    name: 'Hero Banner',
    page: 'Home',
    description: 'Cover photo, club name, and welcome message',
    fields: [
      { key: 'title', label: 'Title', type: 'text', placeholder: 'Your Club Name' },
      { key: 'motto', label: 'Motto', type: 'text', placeholder: 'e.g. Football - Friends - Teamwork' },
      { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Building character and skills through the beautiful game. Join our community of passionate players and families.' },
      { key: 'ctaText', label: 'Button Text', type: 'text', placeholder: 'Join Us Today' },
      { key: 'showJoinBanner', label: 'Join banner under the news', type: 'select', hint: 'A second strip with this description and button, below the latest news on the home page', options: [
        { value: 'yes', label: 'Shown' },
        { value: 'no', label: 'Hidden' },
      ] },
      { key: 'heroImage', label: 'Cover Photo', type: 'image', hint: 'Cover photo behind your club badge — like a Facebook cover', previewStyle: 'hero' },
    ],
  },
  {
    id: 'home-nextmatch',
    name: 'Next Match',
    page: 'Home',
    description: 'Which side the home page counts down to',
    fields: [
      {
        key: 'teamId',
        label: 'Show the next match for',
        type: 'select',
        optionsSource: 'teamsWithFixtures',
        hint: 'Only sides with a fixture still to play are listed. Clubs with more than one team choose a side; nothing appears until they do.',
      },
    ],
  },
  {
    id: 'home-matches',
    name: 'Matchday',
    page: 'Home',
    description: "The week's games for every team, the latest scores, and where one team stands in its league",
    fields: [
      { key: 'show', label: 'Show Matchday on the home page', type: 'select', options: [
        { value: '', label: 'As my design decides (shown with Victory)' },
        { value: 'on', label: 'Show' },
        { value: 'off', label: 'Hide' },
      ], hint: 'Move it up or down the page under Home page order.' },
      { key: 'teamId', label: 'Where we stand: which team', type: 'select', optionsSource: 'teams',
        hint: 'Its league position (from its league table link) and season record. Left as it is, it follows your Next Match team, then a team called First Team.' },
    ],
  },
  {
    id: 'home-about',
    name: 'About Section',
    page: 'Home',
    description: 'Welcome text and club stats',
    fields: [
      { key: 'title', label: 'Title', type: 'text', placeholder: 'About Your Club' },
      { key: 'paragraph1', label: 'First Paragraph', type: 'textarea', rich: true, placeholder: 'Your club is a community-based football club dedicated to developing young players in a fun, supportive, and competitive environment.' },
      { key: 'paragraph2', label: 'Second Paragraph', type: 'textarea', rich: true, placeholder: 'Our experienced coaches work with players of all abilities, from first steps in football to competing at higher levels.' },
      { key: 'statTeams', label: 'Teams Stat', type: 'text', placeholder: '10+' },
      { key: 'statPlayers', label: 'Players Stat', type: 'text', placeholder: '200+' },
      { key: 'statCoaches', label: 'Coaches Stat', type: 'text', placeholder: '25+' },
      { key: 'statTeamsLabel', label: 'Teams Stat Label', type: 'text', placeholder: 'Teams' },
      { key: 'statPlayersLabel', label: 'Players Stat Label', type: 'text', placeholder: 'Players' },
      { key: 'statCoachesLabel', label: 'Coaches Stat Label', type: 'text', placeholder: 'Coaches' },
      { key: 'image', label: 'About Image', type: 'image', hint: 'Team photo shown beside the text. Add a second below and they rotate automatically.', previewStyle: 'photo' },
      { key: 'image2', label: 'About Image 2 (optional)', type: 'image', hint: 'Adding this turns the image into a slideshow — it and the ones below fade in turn.', previewStyle: 'photo' },
      { key: 'image3', label: 'About Image 3 (optional)', type: 'image', hint: 'Only used if image 2 is set.', previewStyle: 'photo' },
      { key: 'image4', label: 'About Image 4 (optional)', type: 'image', hint: 'Only used if image 2 is set.', previewStyle: 'photo' },
    ],
  },
  {
    id: 'home-cta',
    name: 'Call to Action',
    page: 'Home',
    description: 'Bottom section encouraging visitors to join',
    fields: [
      { key: 'title', label: 'Title', type: 'text', placeholder: 'Ready to Join?' },
      { key: 'description', label: 'Description', type: 'textarea', placeholder: "Whether you're starting your football journey or developing your skills, your club has a place for you." },
      { key: 'primaryButtonText', label: 'Primary Button', type: 'text', placeholder: 'Register Now' },
      { key: 'secondaryButtonText', label: 'Secondary Button', type: 'text', placeholder: 'View Teams' },
    ],
  },

  {
    id: 'home-video',
    name: 'Featured Video',
    page: 'Home',
    description: 'Optional video section with a YouTube or Vimeo embed',
    fields: [
      { key: 'title', label: 'Section Title', type: 'text', placeholder: 'Watch Us Play' },
      { key: 'description', label: 'Description', type: 'textarea', placeholder: 'See our club in action.' },
      { key: 'videoUrl', label: 'Video URL', type: 'video', hint: 'YouTube or Vimeo URL (optional)' },
    ],
  },

  // ── Teams Page ──────────────────────────────
  {
    id: 'teams-header',
    name: 'Page Header & CTA',
    page: 'Teams',
    description: 'Title, description, empty state, and join call-to-action',
    fields: [
      { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Our Teams' },
      { key: 'subtitle', label: 'Subtitle', type: 'textarea', placeholder: 'Your club offers teams for players of all ages and abilities. From grassroots to competitive football, we have a team for everyone.' },
      { key: 'emptyMessage', label: 'Empty State Message', type: 'text', placeholder: 'No teams available at the moment. Please check back later.' },
      { key: 'ctaHeading', label: 'CTA Heading', type: 'text', placeholder: 'Interested in Joining a Team?' },
      { key: 'ctaDescription', label: 'CTA Description', type: 'textarea', placeholder: 'We welcome players of all abilities. Get in touch to find out more about trials and registration for the upcoming season.' },
    ],
  },

  // ── News Page ────────────────────────────────
  {
    id: 'news-header',
    name: 'Page Header',
    page: 'News',
    description: 'Title, subtitle, and empty state message',
    fields: [
      { key: 'title', label: 'Page Title', type: 'text', placeholder: 'News & Updates' },
      { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: 'The latest from your club' },
      { key: 'emptyMessage', label: 'Empty State Message', type: 'text', placeholder: 'No news articles yet. Check back soon for updates!' },
    ],
  },

  // ── Home Ground ────────────────────────────────
  {
    id: 'home-ground',
    name: 'Ground Details',
    page: 'Home Ground',
    description: 'Address, directions, and training times',
    fields: [
      { key: 'groundName', label: 'Ground Name', type: 'text', placeholder: 'Our Home Ground' },
      { key: 'subtitle', label: 'Page Subtitle', type: 'text', placeholder: 'Where we play and train' },
      { key: 'addressLine1', label: 'Address Line 1', type: 'text', placeholder: 'e.g. Dodford Lane' },
      { key: 'addressLine2', label: 'Address Line 2', type: 'text', placeholder: 'e.g. Cambridge' },
      { key: 'postcode', label: 'Postcode', type: 'text', placeholder: 'e.g. CB3 0QH' },
      { key: 'what3words', label: 'what3words', type: 'text', placeholder: 'e.g. ///filled.count.soap',
        hint: 'Three words for the exact spot: the car park entrance, or the pitch itself. Find yours at what3words.com and paste it here. A postcode gets people to the rec; this gets them to the right corner of it.' },
      { key: 'mapLocation', label: 'Exact map location', type: 'text', placeholder: 'e.g. 52.486719, -0.464055',
        hint: 'Optional. Puts the map pin and the directions link on the pitch instead of the postcode. In Google Maps, right-click the pitch and click the numbers at the top of the menu to copy them, then paste here. A Google Maps link with a dropped pin works too.' },
      { key: 'addressCardTitle', label: 'Address Card Title', type: 'text', placeholder: 'Address' },
      { key: 'trainingCardTitle', label: 'Training Card Title', type: 'text', placeholder: 'Training Times' },
      { key: 'gettingHereCardTitle', label: 'Getting Here Card Title', type: 'text', placeholder: 'Getting Here' },
      { key: 'gettingHere', label: 'Getting Here', type: 'textarea', rich: true, placeholder: 'Contact the club for directions and travel information.' },
      { key: 'trainingTimes', label: 'Training Times', type: 'textarea', rich: true, placeholder: 'Training sessions run on weekday evenings and weekend mornings. Contact your team coach for exact times.' },
      { key: 'findUsHeading', label: 'Map Section Heading', type: 'text', placeholder: 'Find Us' },
    ],
  },
  {
    id: 'home-ground-facilities',
    name: 'Facilities & Visit CTA',
    page: 'Home Ground',
    description: 'Facility items and the visit call-to-action',
    fields: [
      { key: 'facilitiesHeading', label: 'Facilities Heading', type: 'text', placeholder: 'Facilities' },
      { key: 'facility1Title', label: 'Facility 1 - Title', type: 'text', placeholder: 'Pitches' },
      { key: 'facility1Desc', label: 'Facility 1 - Description', type: 'text', placeholder: 'Well-maintained grass pitches for all age groups' },
      { key: 'facility2Title', label: 'Facility 2 - Title', type: 'text', placeholder: 'Changing Rooms' },
      { key: 'facility2Desc', label: 'Facility 2 - Description', type: 'text', placeholder: 'Modern facilities with hot showers' },
      { key: 'facility3Title', label: 'Facility 3 - Title', type: 'text', placeholder: 'Clubhouse' },
      { key: 'facility3Desc', label: 'Facility 3 - Description', type: 'text', placeholder: 'Refreshments for players, parents, and supporters' },
      { key: 'facility4Title', label: 'Facility 4 - Title', type: 'text', placeholder: 'Parking' },
      { key: 'facility4Desc', label: 'Facility 4 - Description', type: 'text', placeholder: 'Ample car parking adjacent to the ground' },
      { key: 'facility5Title', label: 'Facility 5 - Title', type: 'text', placeholder: '(Optional)' },
      { key: 'facility5Desc', label: 'Facility 5 - Description', type: 'text', placeholder: '(Optional)' },
      { key: 'facility6Title', label: 'Facility 6 - Title', type: 'text', placeholder: '(Optional)' },
      { key: 'facility6Desc', label: 'Facility 6 - Description', type: 'text', placeholder: '(Optional)' },
      { key: 'visitCtaHeading', label: 'Visit CTA Heading', type: 'text', placeholder: 'Come and Visit' },
      { key: 'visitCtaDescription', label: 'Visit CTA Description', type: 'textarea', placeholder: 'All spectators are welcome on match days. Get in touch to arrange a visit.' },
    ],
  },

  // ── Join Page ──────────────────────────────────
  {
    id: 'join-header',
    name: 'Page Header',
    page: 'Join Us',
    description: 'Title and intro text at the top',
    fields: [
      { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Join Your Club' },
      { key: 'subtitle', label: 'Subtitle', type: 'textarea', placeholder: "Start your football journey with us. Register your interest and we'll be in touch." },
      { key: 'whyJoinTitle', label: '"Why Join" Heading', type: 'text', placeholder: 'Why Join Your Club?' },
    ],
  },
  {
    id: 'join-benefits',
    name: 'Benefits',
    page: 'Join Us',
    description: 'Reasons to join your club. Up to 12 — leave any unused slots blank and they will be hidden on the public page.',
    fields: [
      { key: 'benefit1Title', label: 'Benefit 1 - Title', type: 'text', placeholder: 'Qualified Coaches' },
      { key: 'benefit1Desc', label: 'Benefit 1 - Description', type: 'text', placeholder: 'All our coaches are FA qualified with enhanced DBS checks' },
      { key: 'benefit2Title', label: 'Benefit 2 - Title', type: 'text', placeholder: 'Age-Appropriate Development' },
      { key: 'benefit2Desc', label: 'Benefit 2 - Description', type: 'text', placeholder: 'Structured training programs designed for each age group' },
      { key: 'benefit3Title', label: 'Benefit 3 - Title', type: 'text', placeholder: 'Competitive & Friendly Matches' },
      { key: 'benefit3Desc', label: 'Benefit 3 - Description', type: 'text', placeholder: 'Regular fixtures in local leagues and friendly tournaments' },
      { key: 'benefit4Title', label: 'Benefit 4 - Title', type: 'text', placeholder: 'Excellent Facilities' },
      { key: 'benefit4Desc', label: 'Benefit 4 - Description', type: 'text', placeholder: 'Well-maintained pitches, changing rooms, and clubhouse' },
      { key: 'benefit5Title', label: 'Benefit 5 - Title', type: 'text', placeholder: 'Community Focus' },
      { key: 'benefit5Desc', label: 'Benefit 5 - Description', type: 'text', placeholder: 'A friendly, inclusive environment for all abilities' },
      { key: 'benefit6Title', label: 'Benefit 6 - Title', type: 'text', placeholder: 'Affordable Fees' },
      { key: 'benefit6Desc', label: 'Benefit 6 - Description', type: 'text', placeholder: 'Competitive subscription rates with family discounts available' },
      { key: 'benefit7Title', label: 'Benefit 7 - Title (optional)', type: 'text', placeholder: 'Leave blank to hide' },
      { key: 'benefit7Desc', label: 'Benefit 7 - Description (optional)', type: 'text', placeholder: '' },
      { key: 'benefit8Title', label: 'Benefit 8 - Title (optional)', type: 'text', placeholder: 'Leave blank to hide' },
      { key: 'benefit8Desc', label: 'Benefit 8 - Description (optional)', type: 'text', placeholder: '' },
      { key: 'benefit9Title', label: 'Benefit 9 - Title (optional)', type: 'text', placeholder: 'Leave blank to hide' },
      { key: 'benefit9Desc', label: 'Benefit 9 - Description (optional)', type: 'text', placeholder: '' },
      { key: 'benefit10Title', label: 'Benefit 10 - Title (optional)', type: 'text', placeholder: 'Leave blank to hide' },
      { key: 'benefit10Desc', label: 'Benefit 10 - Description (optional)', type: 'text', placeholder: '' },
      { key: 'benefit11Title', label: 'Benefit 11 - Title (optional)', type: 'text', placeholder: 'Leave blank to hide' },
      { key: 'benefit11Desc', label: 'Benefit 11 - Description (optional)', type: 'text', placeholder: '' },
      { key: 'benefit12Title', label: 'Benefit 12 - Title (optional)', type: 'text', placeholder: 'Leave blank to hide' },
      { key: 'benefit12Desc', label: 'Benefit 12 - Description (optional)', type: 'text', placeholder: '' },
    ],
  },
  {
    id: 'join-form-visibility',
    name: 'Default Form Fields (Show / Hide)',
    page: 'Join Us',
    description: 'Choose which default questions appear on the "Register Your Interest" form. Player Name and Email are always shown. Anything you hide is removed from the form entirely.',
    fields: [
      { key: 'showDateOfBirth', label: 'Date of Birth', type: 'select', options: [
        { value: 'yes', label: 'Show (required)' },
        { value: 'no', label: 'Hide' },
      ] },
      { key: 'showParentName', label: 'Parent / Guardian Name', type: 'select', options: [
        { value: 'yes', label: 'Show (required)' },
        { value: 'no', label: 'Hide' },
      ] },
      { key: 'showPhone', label: 'Phone Number', type: 'select', options: [
        { value: 'yes', label: 'Show (required)' },
        { value: 'no', label: 'Hide' },
      ] },
      { key: 'showAgeGroup', label: 'Preferred Age Group', type: 'select', options: [
        { value: 'yes', label: 'Show (required)' },
        { value: 'no', label: 'Hide' },
      ] },
      { key: 'showExperience', label: 'Football Experience', type: 'select', options: [
        { value: 'yes', label: 'Show (optional)' },
        { value: 'no', label: 'Hide' },
      ] },
      { key: 'showMessage', label: 'Additional Information', type: 'select', options: [
        { value: 'yes', label: 'Show (optional)' },
        { value: 'no', label: 'Hide' },
      ] },
    ],
  },
  {
    id: 'join-form-custom',
    name: 'Custom Form Questions',
    page: 'Join Us',
    description: 'Up to 5 additional questions to ask on the "Register Your Interest" form. Please don\'t ask about medical conditions, allergies or anything else health-related here — those answers are special category data under UK GDPR and need a lawful condition beyond ordinary consent. Collect them from parents once a player has actually joined, not on a public form. Type the question — it becomes a text field on the public form. The answers are included in the email you receive. Leave any slot blank to hide it.',
    fields: [
      { key: 'custom1Label', label: 'Question 1', type: 'text', placeholder: 'e.g. Preferred foot (Left / Right / Both)' },
      { key: 'custom2Label', label: 'Question 2', type: 'text', placeholder: 'e.g. How did you hear about us?' },
      { key: 'custom3Label', label: 'Question 3', type: 'text', placeholder: 'e.g. Current club (if any)' },
      { key: 'custom4Label', label: 'Question 4', type: 'text', placeholder: 'e.g. Emergency contact name and number' },
      { key: 'custom5Label', label: 'Question 5', type: 'text', placeholder: '' },
    ],
  },
  {
    id: 'join-info',
    name: 'Season Info & Registration Process',
    page: 'Join Us',
    description: 'Registration process, season details, and success message',
    fields: [
      { key: 'seasonInfoTitle', label: 'Season Info Heading', type: 'text', placeholder: 'Season Information' },
      { key: 'seasonRuns', label: 'Season Runs', type: 'text', placeholder: 'September to May' },
      { key: 'training', label: 'Training', type: 'text', placeholder: 'Weekly sessions (times vary by age group)' },
      { key: 'matches', label: 'Matches', type: 'text', placeholder: 'Weekends during the season' },
      { key: 'fees', label: 'Fees', type: 'text', placeholder: 'Contact us for current rates' },
      { key: 'whatsNextTitle', label: '"What Happens Next" Heading', type: 'text', placeholder: 'What Happens Next?' },
      { key: 'whatsNextStep1', label: 'Step 1', type: 'text', placeholder: 'Complete the registration form' },
      { key: 'whatsNextStep2', label: 'Step 2', type: 'text', placeholder: "We'll review your application and contact you within 48 hours" },
      { key: 'whatsNextStep3', label: 'Step 3', type: 'text', placeholder: 'Attend a trial session to meet the coach and team' },
      { key: 'whatsNextStep4', label: 'Step 4', type: 'text', placeholder: 'Complete registration and join your team' },
      { key: 'whatsNextStep5', label: 'Step 5', type: 'text', placeholder: '(Optional) Additional step' },
      { key: 'whatsNextStep6', label: 'Step 6', type: 'text', placeholder: '(Optional) Additional step' },
      { key: 'successTitle', label: 'Success Title', type: 'text', placeholder: 'Thank You!' },
      { key: 'successMessage', label: 'Success Message', type: 'textarea', placeholder: "Your registration has been received. We'll be in touch within 48 hours to discuss next steps." },
    ],
  },

  // ── Shop Page ───────────────────────────────
  {
    id: 'shop-header',
    name: 'Page Header',
    page: 'Shop',
    description: 'Title, subtitle, and empty state message',
    fields: [
      { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Club Shop' },
      { key: 'subtitle', label: 'Subtitle', type: 'textarea', placeholder: 'Official merchandise. Show your support with our range of kit and accessories.' },
      { key: 'emptyMessage', label: 'Empty State Message', type: 'text', placeholder: 'No products available in this category.' },
    ],
  },
  {
    id: 'cart-header',
    name: 'Cart Page',
    page: 'Shop',
    description: 'Cart page title and empty state',
    fields: [
      { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Shopping Cart' },
      { key: 'emptyHeading', label: 'Empty Cart Heading', type: 'text', placeholder: 'Your cart is empty' },
      { key: 'emptyMessage', label: 'Empty Cart Message', type: 'text', placeholder: 'Add some items to get started!' },
    ],
  },
  {
    id: 'order-success',
    name: 'Order Success',
    page: 'Shop',
    description: 'Order confirmation page content',
    fields: [
      { key: 'pageTitle', label: 'Page Title', type: 'text', placeholder: 'Order Successful!' },
      { key: 'thankYouHeading', label: 'Thank You Heading', type: 'text', placeholder: 'Thank you for your purchase' },
      { key: 'thankYouMessage', label: 'Thank You Message', type: 'textarea', placeholder: 'Your order has been received and is being processed. You will receive a confirmation email shortly with your order details and tracking information.' },
      { key: 'whatsNextHeading', label: '"What\'s Next" Heading', type: 'text', placeholder: 'What happens next?' },
      { key: 'whatsNextStep1', label: 'Step 1', type: 'text', placeholder: "You'll receive an order confirmation email" },
      { key: 'whatsNextStep2', label: 'Step 2', type: 'text', placeholder: "We'll process and pack your order" },
      { key: 'whatsNextStep3', label: 'Step 3', type: 'text', placeholder: "You'll get a shipping notification with tracking details" },
      { key: 'whatsNextStep4', label: 'Step 4', type: 'text', placeholder: 'Your order will be delivered to your address' },
      { key: 'needHelpHeading', label: 'Need Help Heading', type: 'text', placeholder: 'Need help?' },
      { key: 'needHelpMessage', label: 'Need Help Message', type: 'textarea', placeholder: "If you have any questions about your order, please don't hesitate to contact us." },
      { key: 'supportHeading', label: 'Support Heading', type: 'text', placeholder: 'Support Your Club' },
      { key: 'supportMessage', label: 'Support Message', type: 'textarea', placeholder: 'Thank you for supporting our club! Your purchase helps fund our teams, coaching programs, and facilities, making a real difference to young players in our community.' },
    ],
  },

  // ── Safeguarding ───────────────────────────────
  {
    id: 'safeguarding-header',
    name: 'Page Header & Intro',
    page: 'Safeguarding',
    description: 'Title, subtitle, and introduction text',
    fields: [
      { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Safeguarding & Child Protection' },
      { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: 'The welfare and safety of all children at our club is our paramount concern.' },
      { key: 'intro', label: 'Introduction', type: 'textarea', placeholder: 'Our club is committed to ensuring that football is safe and enjoyable for everyone. We have a duty of care to safeguard all children involved in football and promote their welfare.' },
      { key: 'commitmentIntro', label: 'Commitment Intro', type: 'textarea', placeholder: 'We recognise our moral and legal obligation to provide a duty of care, to safeguard and promote the welfare of children, and to work in a way that protects them from harm. We are committed to:' },
      { key: 'commitment1', label: 'Commitment 1', type: 'textarea', placeholder: 'Creating a safe and welcoming environment, both on and off the pitch, where children can have fun and develop their skills.' },
      { key: 'commitment2', label: 'Commitment 2', type: 'textarea', placeholder: 'Recognising that safeguarding children is the responsibility of everyone, not just those who work with children.' },
      { key: 'commitment3', label: 'Commitment 3', type: 'textarea', placeholder: 'Ensuring all staff and volunteers are carefully selected, trained, and supervised.' },
      { key: 'commitment4', label: 'Commitment 4', type: 'textarea', placeholder: 'Responding swiftly and appropriately to all complaints and concerns about poor practice or suspected or actual child abuse.' },
      { key: 'commitment5', label: 'Commitment 5', type: 'textarea', placeholder: '(Optional)' },
      { key: 'commitment6', label: 'Commitment 6', type: 'textarea', placeholder: '(Optional)' },
      { key: 'commitment7', label: 'Commitment 7', type: 'textarea', placeholder: '(Optional)' },
      { key: 'commitment8', label: 'Commitment 8', type: 'textarea', placeholder: '(Optional)' },
    ],
  },
  {
    id: 'safeguarding-measures',
    name: 'Measures & Policies',
    page: 'Safeguarding',
    description: 'The 6 safeguarding measure cards',
    fields: [
      { key: 'measure1Title', label: 'Measure 1 - Title', type: 'text', placeholder: 'DBS Checks' },
      { key: 'measure1Desc', label: 'Measure 1 - Description', type: 'textarea', placeholder: 'All coaches, volunteers, and anyone in a position of trust undergo enhanced Disclosure and Barring Service (DBS) checks.' },
      { key: 'measure2Title', label: 'Measure 2 - Title', type: 'text', placeholder: 'Qualified Coaches' },
      { key: 'measure2Desc', label: 'Measure 2 - Description', type: 'textarea', placeholder: 'All our coaches hold relevant FA coaching qualifications and have completed safeguarding training.' },
      { key: 'measure3Title', label: 'Measure 3 - Title', type: 'text', placeholder: 'Code of Conduct' },
      { key: 'measure3Desc', label: 'Measure 3 - Description', type: 'textarea', placeholder: 'We have clear codes of conduct for coaches, volunteers, parents, and players.' },
      { key: 'measure4Title', label: 'Measure 4 - Title', type: 'text', placeholder: 'Reporting Procedures' },
      { key: 'measure4Desc', label: 'Measure 4 - Description', type: 'textarea', placeholder: 'We have clear procedures for reporting concerns. All concerns are taken seriously and dealt with promptly and appropriately.' },
      { key: 'measure5Title', label: 'Measure 5 - Title', type: 'text', placeholder: 'Photography Policy' },
      { key: 'measure5Desc', label: 'Measure 5 - Description', type: 'textarea', placeholder: 'We have a clear policy on photography and filming at matches and events.' },
      { key: 'measure6Title', label: 'Measure 6 - Title', type: 'text', placeholder: 'Transport Policy' },
      { key: 'measure6Desc', label: 'Measure 6 - Description', type: 'textarea', placeholder: 'Clear guidelines are in place for transporting children to away fixtures and events.' },
    ],
  },
  {
    id: 'safeguarding-contact',
    name: 'Welfare Officer & Policies',
    page: 'Safeguarding',
    description: 'Contact details, anti-bullying, and policy date',
    fields: [
      { key: 'welfareOfficerName', label: 'Welfare Officer Name', type: 'text', placeholder: 'Welfare Officer Name' },
      { key: 'welfareOfficerEmail', label: 'Welfare Officer Email', type: 'text', placeholder: 'welfare@yourclub.co.uk' },
      { key: 'welfareOfficerPhone', label: 'Welfare Officer Phone', type: 'text', placeholder: 'Contact Number' },
      { key: 'antiBullyingParagraph1', label: 'Anti-Bullying - Paragraph 1', type: 'textarea', placeholder: 'Our club has a zero-tolerance approach to bullying. We are committed to providing a caring, friendly, and safe environment for all our members so they can participate in football in a relaxed and secure atmosphere.' },
      { key: 'antiBullyingParagraph2', label: 'Anti-Bullying - Paragraph 2', type: 'textarea', placeholder: 'All children have the right to protection from bullying. Everyone at the club - players, coaches, volunteers, and parents - has a responsibility to help prevent bullying and to respond appropriately if it occurs.' },
      { key: 'policyLastUpdated', label: 'Policy Last Updated', type: 'text', placeholder: 'March 2026' },
    ],
  },
  {
    id: 'safeguarding-sections',
    name: 'Section Headings & Extra Content',
    page: 'Safeguarding',
    description: 'Headings, welfare officer responsibilities, respect programme, and resources',
    fields: [
      { key: 'commitmentHeading', label: 'Commitment Section Heading', type: 'text', placeholder: 'Our Commitment' },
      { key: 'measuresHeading', label: 'Measures Section Heading', type: 'text', placeholder: 'Our Safeguarding Measures' },
      { key: 'welfareHeading', label: 'Welfare Officer Heading', type: 'text', placeholder: 'Club Welfare Officer' },
      { key: 'welfareDescription', label: 'Welfare Officer Description', type: 'textarea', placeholder: 'Our Club Welfare Officer is the first point of contact for all safeguarding concerns at our club. They are responsible for:' },
      { key: 'welfareResp1', label: 'Responsibility 1', type: 'text', placeholder: 'Managing all safeguarding concerns appropriately' },
      { key: 'welfareResp2', label: 'Responsibility 2', type: 'text', placeholder: 'Ensuring all club members are aware of safeguarding procedures' },
      { key: 'welfareResp3', label: 'Responsibility 3', type: 'text', placeholder: 'Maintaining up-to-date safeguarding training' },
      { key: 'welfareResp4', label: 'Responsibility 4', type: 'text', placeholder: 'Liaising with the FA and local safeguarding authorities' },
      { key: 'welfareResp5', label: 'Responsibility 5', type: 'text', placeholder: 'Promoting good safeguarding practice throughout the club' },
      { key: 'welfareResp6', label: 'Responsibility 6', type: 'text', placeholder: '(Optional)' },
      { key: 'welfareResp7', label: 'Responsibility 7', type: 'text', placeholder: '(Optional)' },
      { key: 'welfareResp8', label: 'Responsibility 8', type: 'text', placeholder: '(Optional)' },
      { key: 'reportingHeading', label: 'Reporting Section Heading', type: 'text', placeholder: 'Reporting a Concern' },
      { key: 'reportingDescription', label: 'Reporting Description', type: 'textarea', placeholder: 'If you have any concerns about the welfare of a child at our club, please contact our Club Welfare Officer immediately. All concerns will be treated seriously and confidentially.' },
      { key: 'respectHeading', label: 'Respect Section Heading', type: 'text', placeholder: 'Respect Programme' },
      { key: 'respectDescription', label: 'Respect Description', type: 'textarea', placeholder: "Our club is committed to The FA's Respect programme, which aims to create a fair, safe, and enjoyable environment in which football can take place. This includes:" },
      { key: 'respectItem1', label: 'Respect Item 1', type: 'text', placeholder: 'Promoting respect between players, coaches, referees, and spectators' },
      { key: 'respectItem2', label: 'Respect Item 2', type: 'text', placeholder: 'Challenging inappropriate behaviour and language' },
      { key: 'respectItem3', label: 'Respect Item 3', type: 'text', placeholder: 'Creating designated spectator areas at matches' },
      { key: 'respectItem4', label: 'Respect Item 4', type: 'text', placeholder: 'Implementing sin bins for youth football' },
      { key: 'respectItem5', label: 'Respect Item 5', type: 'text', placeholder: 'Celebrating good behaviour and sportsmanship' },
      { key: 'respectItem6', label: 'Respect Item 6', type: 'text', placeholder: '(Optional)' },
      { key: 'respectItem7', label: 'Respect Item 7', type: 'text', placeholder: '(Optional)' },
      { key: 'respectItem8', label: 'Respect Item 8', type: 'text', placeholder: '(Optional)' },
      { key: 'antiBullyingHeading', label: 'Anti-Bullying Heading', type: 'text', placeholder: 'Anti-Bullying Policy' },
      { key: 'resourcesHeading', label: 'Resources Heading', type: 'text', placeholder: 'Useful Resources' },
      { key: 'policyFooter', label: 'Policy Footer', type: 'textarea', placeholder: 'For a full copy of our Safeguarding Policy and related documents, please contact the Club Welfare Officer.' },
      { key: 'contactCardTitle', label: 'Contact Card Title', type: 'text', placeholder: 'Club Welfare Officer' },
      { key: 'contactCardNote', label: 'Contact Card Note', type: 'textarea', placeholder: 'If you cannot contact the Club Welfare Officer or feel your concern has not been addressed, you can contact:' },
    ],
  },

  {
    id: 'safeguarding-resources',
    name: 'Useful Resources',
    page: 'Safeguarding',
    description: 'External safeguarding resources with optional logos',
    fields: [
      { key: 'resource1Title', label: 'Resource 1 — Title', type: 'text', placeholder: 'The FA Safeguarding' },
      { key: 'resource1Url', label: 'Resource 1 — URL', type: 'text', placeholder: 'https://www.thefa.com/football-rules-governance/safeguarding' },
      { key: 'resource1Desc', label: 'Resource 1 — Description', type: 'text', placeholder: 'Official FA safeguarding guidelines and resources' },
      { key: 'resource1Logo', label: 'Resource 1 — Logo', type: 'image', hint: 'Optional organisation logo' },
      { key: 'resource2Title', label: 'Resource 2 — Title', type: 'text', placeholder: 'NSPCC Child Protection in Sport' },
      { key: 'resource2Url', label: 'Resource 2 — URL', type: 'text', placeholder: 'https://www.nspcc.org.uk/keeping-children-safe/in-sport/' },
      { key: 'resource2Desc', label: 'Resource 2 — Description', type: 'text', placeholder: 'Expert advice on keeping children safe in sport' },
      { key: 'resource2Logo', label: 'Resource 2 — Logo', type: 'image', hint: 'Optional organisation logo' },
      { key: 'resource3Title', label: 'Resource 3 — Title', type: 'text', placeholder: 'Childline' },
      { key: 'resource3Url', label: 'Resource 3 — URL', type: 'text', placeholder: 'https://www.childline.org.uk' },
      { key: 'resource3Desc', label: 'Resource 3 — Description', type: 'text', placeholder: 'Free, confidential support for young people (0800 1111)' },
      { key: 'resource3Logo', label: 'Resource 3 — Logo', type: 'image', hint: 'Optional organisation logo' },
      { key: 'resource4Title', label: 'Resource 4 — Title', type: 'text', placeholder: '(Optional)' },
      { key: 'resource4Url', label: 'Resource 4 — URL', type: 'text', placeholder: 'https://...' },
      { key: 'resource4Desc', label: 'Resource 4 — Description', type: 'text', placeholder: '(Optional)' },
      { key: 'resource4Logo', label: 'Resource 4 — Logo', type: 'image', hint: 'Optional organisation logo' },
      { key: 'resource5Title', label: 'Resource 5 — Title', type: 'text', placeholder: '(Optional)' },
      { key: 'resource5Url', label: 'Resource 5 — URL', type: 'text', placeholder: 'https://...' },
      { key: 'resource5Desc', label: 'Resource 5 — Description', type: 'text', placeholder: '(Optional)' },
      { key: 'resource5Logo', label: 'Resource 5 — Logo', type: 'image', hint: 'Optional organisation logo' },
      { key: 'resource6Title', label: 'Resource 6 — Title', type: 'text', placeholder: '(Optional)' },
      { key: 'resource6Url', label: 'Resource 6 — URL', type: 'text', placeholder: 'https://...' },
      { key: 'resource6Desc', label: 'Resource 6 — Description', type: 'text', placeholder: '(Optional)' },
      { key: 'resource6Logo', label: 'Resource 6 — Logo', type: 'image', hint: 'Optional organisation logo' },
    ],
  },

  // ── Contact & Footer ──────────────────────────
  {
    id: 'contact-page',
    name: 'Contact Page Text',
    page: 'Footer / Contact',
    description: 'Intro and response-time text shown on the public Contact page (left side, next to the form).',
    fields: [
      { key: 'intro', label: '"Get in Touch" Intro', type: 'textarea', rich: true, placeholder: 'Whether you have a question about joining, training schedules, match days, or anything else — our team is ready to help.' },
      { key: 'responseTime', label: 'Response Time Note', type: 'text', placeholder: 'We aim to respond to all enquiries within 48 hours.' },
    ],
  },
  {
    id: 'contact-info',
    name: 'Contact & Social',
    page: 'Footer / Contact',
    description: 'Contact details and social media links',
    fields: [
      { key: 'tagline', label: 'Tagline', type: 'text', placeholder: 'e.g. Football - Friends - Teamwork' },
      { key: 'description', label: 'Footer Description', type: 'textarea', placeholder: 'Your local football club, bringing the community together through the beautiful game.' },
      { key: 'contactEmail', label: 'Contact Email', type: 'text', placeholder: 'info@yourclub.com' },
      { key: 'contactPhone', label: 'Contact Phone', type: 'text', placeholder: '01234 567890' },
      { key: 'facebook', label: 'Facebook URL', type: 'text', placeholder: 'https://facebook.com/yourclub' },
      { key: 'twitter', label: 'Twitter / X URL', type: 'text', placeholder: 'https://twitter.com/yourclub' },
      { key: 'instagram', label: 'Instagram URL', type: 'text', placeholder: 'https://instagram.com/yourclub' },
      { key: 'social4Platform', label: 'Social 4 — Platform', type: 'select', placeholder: 'Select platform', options: [
        { value: '', label: '— None —' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'whatsapp', label: 'WhatsApp' },
        { value: 'snapchat', label: 'Snapchat' },
        { value: 'threads', label: 'Threads' },
      ] },
      { key: 'social4Url', label: 'Social 4 — URL', type: 'text', placeholder: 'https://...' },
      { key: 'social5Platform', label: 'Social 5 — Platform', type: 'select', placeholder: 'Select platform', options: [
        { value: '', label: '— None —' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'whatsapp', label: 'WhatsApp' },
        { value: 'snapchat', label: 'Snapchat' },
        { value: 'threads', label: 'Threads' },
      ] },
      { key: 'social5Url', label: 'Social 5 — URL', type: 'text', placeholder: 'https://...' },
      { key: 'social6Platform', label: 'Social 6 — Platform', type: 'select', placeholder: 'Select platform', options: [
        { value: '', label: '— None —' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'whatsapp', label: 'WhatsApp' },
        { value: 'snapchat', label: 'Snapchat' },
        { value: 'threads', label: 'Threads' },
      ] },
      { key: 'social6Url', label: 'Social 6 — URL', type: 'text', placeholder: 'https://...' },
    ],
  },

  // ── Announcements ─────────────────────────────
  {
    id: 'announcements',
    name: 'Announcement Banner',
    page: 'Site-wide',
    description: 'Show a banner across the top of the site',
    fields: [
      { key: 'title', label: 'Banner Title', type: 'text', placeholder: 'Important Notice' },
      { key: 'message', label: 'Banner Message', type: 'textarea', placeholder: 'Training cancelled this Saturday due to weather.' },
      { key: 'showBanner', label: 'Show Banner', type: 'select', hint: 'The banner appears across the top of every page', options: [
        { value: 'no', label: 'Hidden' },
        { value: 'yes', label: 'Visible on the site' },
      ] },
      { key: 'linkText', label: 'Link Text', type: 'text', placeholder: 'Find out more', hint: 'Optional button shown at the end of the banner' },
      { key: 'linkUrl', label: 'Link URL', type: 'text', placeholder: '/fixtures', hint: 'Optional. Use /fixtures for a page on your site, or a full https:// address for anywhere else' },
    ],
  },
];

// Group sections by page for sidebar display
export function getSectionsByPage(): { page: string; sections: SectionSchema[] }[] {
  const pageMap = new Map<string, SectionSchema[]>();
  for (const section of PAGE_SECTIONS) {
    const existing = pageMap.get(section.page) || [];
    existing.push(section);
    pageMap.set(section.page, existing);
  }
  return Array.from(pageMap.entries()).map(([page, sections]) => ({ page, sections }));
}

export function getSchemaById(id: string): SectionSchema | undefined {
  return PAGE_SECTIONS.find(s => s.id === id);
}
